(() => {
  'use strict';

  if (window.AWBrandOwnedContextBridge?.version) return;

  const VERSION = 'AW-BRAND-OWNED-CONTEXT-P2-V1';
  const STORAGE_KEY = 'aw_brand_compare_owned_exact_v1';
  const EXACT_SOURCES = new Set(['order', 'catalog-current']);
  const MAX_ITEMS = 24;

  const clean = (value = '', max = 180) => String(value || '').trim().slice(0, max);
  const normalize = (value = '') => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const captureItem = (node) => {
    const source = clean(node.dataset.exactProofSource, 40);
    const modelHandle = clean(node.dataset.modelHandle, 140);
    const modelLabel = clean(node.dataset.model, 180);
    if (!EXACT_SOURCES.has(source) || (!modelHandle && !modelLabel)) return null;

    const platform = clean(node.dataset.platform, 120);
    const platformLabel = clean(node.dataset.platformLabel || node.dataset.platform, 120);
    const manufacturer = clean(node.dataset.manufacturer, 120);
    const replicaType = clean(node.dataset.replicaType, 120);
    const productId = clean(node.dataset.productId, 64);
    const variantId = clean(node.dataset.variantId, 64);
    const identity = modelHandle || normalize(modelLabel);

    return {
      id: `${source}:${identity}:${productId}:${variantId}`,
      label: modelLabel || modelHandle,
      modelHandle,
      modelLabel,
      platform,
      platformLabel,
      manufacturer,
      replicaType,
      source
    };
  };

  const readExisting = () => {
    try {
      const payload = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (Array.isArray(payload)) return payload;
      return Array.isArray(payload?.items) ? payload.items : [];
    } catch (_) {
      return [];
    }
  };

  const write = (items) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: VERSION,
        capturedAt: new Date().toISOString(),
        policy: 'session-only;exact-order-proof-only;no-pii',
        items: items.slice(0, MAX_ITEMS)
      }));
      window.dispatchEvent(new CustomEvent('aw:brand-owned-context-ready', {
        detail: { version: VERSION, count: items.length }
      }));
      return true;
    } catch (_) {
      return false;
    }
  };

  const capture = () => {
    const nodes = [...document.querySelectorAll('[data-aw-shopify-order-item]')];
    if (!nodes.length) return 0;

    const merged = new Map();
    readExisting().forEach((item) => {
      if (item?.id && item?.modelHandle && EXACT_SOURCES.has(item.source)) merged.set(item.id, item);
    });

    nodes.map(captureItem).filter(Boolean).forEach((item) => merged.set(item.id, item));
    const items = [...merged.values()].slice(0, MAX_ITEMS);
    if (items.length) write(items);
    return items.length;
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      capture();
    });
  };

  window.AWBrandOwnedContextBridge = {
    version: VERSION,
    storageKey: STORAGE_KEY,
    capture,
    policy: 'session-only;exact-order-proof-only;no-pii'
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', capture, { once: true });
  } else {
    capture();
  }

  document.addEventListener('shopify:section:load', schedule);
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
        node.nodeType === 1 && (
          node.matches?.('[data-aw-shopify-order-item]') ||
          node.querySelector?.('[data-aw-shopify-order-item]')
        )
      ))) schedule();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
